import { Injectable } from '@angular/core';
import { UserService } from '@app/core/services/user.service';
import { PcsTranslateService } from '@app/core/translate/translate.service';
import { GridType } from '@app/pcs/models';
import {
  DossierNameLinkComponent
} from '@app/pcs/shared/components/dossier-name-link-column/dossier-name-link.component';
import {
  CarryForwardColumnComponent
} from '@app/pcs/shared/containers/search-results-grid/carry-forward-column.component';
import {
  SelectGridFilterProps
} from '@app/pcs/shared/containers/search-results-grid/filters/base-select-grid-filter.directive';
import {
  SingleSelectGridFilterComponent
} from '@app/pcs/shared/containers/search-results-grid/filters/single-select-grid-filter.component';
import { LabelsColumnComponent } from '@app/pcs/shared/containers/search-results-grid/labels-column.component';
import { LabelsFilterComponent } from '@app/pcs/shared/containers/search-results-grid/labels-filter.component';
import {
  ObservationTypesColumnComponent
} from '@app/pcs/shared/containers/search-results-grid/observation-types-column.component';
import {
  ObservationTypesFilterComponent
} from '@app/pcs/shared/containers/search-results-grid/observation-types-filter.component';
import {
  TrafficLightsColumnComponent
} from '@app/pcs/shared/containers/search-results-grid/traffic-lights-column.component';
import {
  TrafficLightsExtendedColumnComponent
} from '@app/pcs/shared/containers/search-results-grid/traffic-lights-extended-column.component';
import { columnFactory } from '@app/pcs/util';
import { hasValue, joinByComma } from '@util';
import { dateComparator } from '@util/comparators';
import { dateFromArray, formatDateArray, FULL_DATE_TIME_FORMAT } from '@util/date-util';
import { FilterType } from 'ngrx-data-grid';
import * as R from 'ramda';

@Injectable()
export class DossierSearchGridColumnsService {
  private column = columnFactory(this.translateService);

  readonly TimetablePeriodId = this.column({
    headerName: 'timetable.period.label',
    field: 'ttpId',
    visible: false,
    filterAvailable: true,
    filter: {
      filterType: FilterType.Number
    }
  });

  readonly TrafficLightsExtended = this.column({
    headerName: 'dossierSearch.trafficLightsExtended',
    field: 'trafficLightsExtended',
    valueGetter: R.prop<any>('trafficLights'),
    visible: false,
    component: TrafficLightsExtendedColumnComponent,
    width: 300
  });

  readonly TrafficLights = this.column({
    headerName: 'label.dossier.traffic.lights',
    field: 'trafficLights',
    visible: true,
    component: TrafficLightsColumnComponent
  });

  readonly TrainNumber = this.column({
    headerName: 'label.dossier.international.train.number',
    field: 'internationalTrainNumber',
    visible: true,
    sortAvailable: true,
    filterAvailable: true,
    filter: {
      filterType: FilterType.Text
    }
  });

  readonly PapId = this.column({
    headerName: 'dashboard.papIdCatalogueId',
    field: 'papId',
    sortAvailable: true,
    visible: false
  });

  readonly DossierName = this.column({
    headerName: 'general.label.name',
    field: 'title',
    visible: true,
    component: DossierNameLinkComponent,
    sortAvailable: true,
    filterAvailable: true,
    filter: {
      filterType: FilterType.Text
    }
  });

  readonly DossierStatus = this.column({
    headerName: 'label.dossier.phase',
    field: 'dossierStatuses',
    visible: true,
    valueGetter: ({dossierStatuses}: any) => hasValue(dossierStatuses)
      ? this.translateDossierStatuses(dossierStatuses)
      : '',
    sortAvailable: true,
    filterAvailable: true,
    filter: {
      component: SingleSelectGridFilterComponent,
      props: {
        propertyPath: ['dossierStatuses'],
        gridName: GridType.Search,
        displayProperty: 'description'
      } as SelectGridFilterProps
    }
  });

  readonly ProcessType = this.column({
    headerName: 'label.dossier.process.type',
    field: 'processType',
    visible: true,
    valueGetter: ({processType}: any) => this.translateService.translate(processType.description),
    sortAvailable: true,
    filterAvailable: true,
    filter: {
      component: SingleSelectGridFilterComponent,
      props: {
        propertyPath: ['processType'],
        gridName: GridType.Search,
        displayProperty: 'description'
      } as SelectGridFilterProps
    }
  });

  readonly DossierType = this.column({
    headerName: 'general.label.dossier.type',
    field: 'dossierType',
    visible: true,
    valueGetter: ({dossierType}: any) => this.translateService.translate(dossierType.description),
    sortAvailable: true,
    filterAvailable: true,
    filter: {
      component: SingleSelectGridFilterComponent,
      props: {
        propertyPath: ['dossierType'],
        gridName: GridType.Search,
        displayProperty: 'description'
      } as SelectGridFilterProps
    }
  });

  readonly TrainType = this.column({
    headerName: 'general.label.train.type',
    field: 'trainType',
    visible: false,
    valueGetter: ({trainType}: any) => this.translateService.translate(trainType.description),
    filterAvailable: true,
    sortAvailable: true,
    filter: {
      component: SingleSelectGridFilterComponent,
      props: {
        propertyPath: ['trainType'],
        gridName: GridType.Search,
        displayProperty: 'description',
      } as SelectGridFilterProps
    }
  });

  readonly DossierIdAndVersion = this.column({
    headerName: 'general.label.id.version',
    field: 'dossierId',
    visible: true,
    valueGetter: ({dossierId, version}: any) => `${dossierId}/${version}`
  });

  readonly LastChangeDate = this.column({
    headerName: 'dashboard.grid.columns.lastChangeDate',
    field: 'lastChangeDate',
    visible: true,
    sortAvailable: true,
    valueGetter: ({lastChangeDate}: any) => formatDateArray(lastChangeDate, FULL_DATE_TIME_FORMAT),
    comparator: (left: any, right: any) => dateComparator(
      dateFromArray(left.lastChangeDate),
      dateFromArray(right.lastChangeDate)
    ),
    width: 155
  });

  readonly LastChangeAgency = this.column({
    headerName: 'dashboard.grid.columns.lastChangeAgency',
    field: 'lastChangeAgency',
    visible: false,
    valueGetter: ({lastChangeAgency}: any) => lastChangeAgency.name,
    filterAvailable: true,
    sortAvailable: true,
    filter: {
      filterType: FilterType.Text
    }
  });

  readonly Observation = this.column({
    headerName: 'dossierstatus.160observations',
    field: 'observation',
    valueGetter: ({observation}: any) => ({...observation, userAgencyId: this.userService.getAgencyId()}),
    visible: false,
    component: ObservationTypesColumnComponent,
    filterAvailable: true,
    filter: {
      component: ObservationTypesFilterComponent,
      props: {
        propertyPath: ['observation', 'observationTypes'],
        displayProperty: 'description',
        gridName: GridType.Search,
        showRowsWithNoOptions: true,
        showRowsWithNoOptionsLabel: 'search.showOnly.dossiersWithNoObservations'
      }
    }
  });

  readonly Labels = this.column({
    headerName: 'label.labels',
    field: 'labels',
    visible: true,
    component: LabelsColumnComponent,
    filterAvailable: true,
    filter: {
      component: LabelsFilterComponent
    }
  });

  readonly CarryForwarded = this.column({
    headerName: 'carryForward.title',
    field: 'carriedForward',
    visible: true,
    component: CarryForwardColumnComponent,
    filterAvailable: true,
    filter: {
      filterType: FilterType.Boolean
    }
  });

  readonly PathRequestLastDate = this.column({
    headerName: 'dossiersearch.pathRequestLastDate',
    field: 'pathRequestLastDate',
    visible: false,
    sortAvailable: true,
    valueGetter: ({pathRequestLastDate}: any) => hasValue(pathRequestLastDate) ? formatDateArray(pathRequestLastDate, FULL_DATE_TIME_FORMAT) : null,
    comparator: (left: any, right: any) => dateComparator(
      dateFromArray(left.pathRequestLastDate),
      dateFromArray(right.pathRequestLastDate)
    )
  });

  constructor(private translateService: PcsTranslateService, private userService: UserService) {
  }

  translateDossierStatuses(dossierStatuses) {
    const translated = R.map(dossierStatus => this.translateService.translate(R.prop('description', dossierStatus)), dossierStatuses);
    return joinByComma(translated);
  }

}
